import type { ParsedQuery } from 'query-string'

import type { QueryParamValue } from '~publish/hooks/useQueryParam'

/**
 * Route query parameter configuration
 * Defines which query params are valid for a route and their possible values
 */
export interface RouteQueryParamConfig {
  readonly param: string
  readonly values?: readonly string[]
}

/**
 * Filters query parameters based on a route's configuration.
 *
 * This utility preserves only the query params that are:
 * 1. Defined in the route's queryParams config
 * 2. Either match the allowed values OR have no value restrictions
 *
 * For parameters with restricted values (values array defined):
 * - Single string values: Preserved if the value is in the allowed list
 * - Array values: Preserved if ALL elements are in the allowed list
 * - Invalid values or partially invalid arrays: Excluded
 *
 * For parameters without restrictions (values: undefined):
 * - All values are preserved as-is (strings or arrays)
 *
 * @param currentSearch - Parsed query parameters from current URL
 * @param routeQueryParams - Route's query parameter configuration
 * @returns Filtered query parameters safe to preserve for the target route
 *
 * @example
 * ```typescript
 * const currentSearch = {
 *   status: 'dismissed',        // Single value for restricted param
 *   tab: ['queue', 'sent'],     // Array for restricted param
 *   foo: 'bar',                 // Not in route config
 *   tags: ['abc', 'xyz']        // Array for unrestricted param
 * }
 *
 * const routeParams = [
 *   { param: 'status', values: ['all', 'dismissed', 'replied'] },
 *   { param: 'tab', values: ['queue', 'sent', 'drafts'] },
 *   { param: 'tags', values: undefined } // no restrictions
 * ]
 *
 * const filtered = filterQueryParamsByRoute(currentSearch, routeParams)
 * // Returns: { status: 'dismissed', tab: ['queue', 'sent'], tags: ['abc', 'xyz'] }
 * // 'foo' is excluded because it's not in routeParams
 * // 'status' is preserved (valid single value)
 * // 'tab' is preserved (all array elements are valid)
 * // 'tags' is preserved (no restrictions)
 * ```
 */
export function filterQueryParamsByRoute(
  currentSearch: ParsedQuery<string | string[] | undefined>,
  routeQueryParams: readonly RouteQueryParamConfig[],
): Record<string, QueryParamValue> {
  const filtered: Record<string, QueryParamValue> = {}

  for (const { param, values: possibleValues } of routeQueryParams) {
    const value = currentSearch[param]

    // Skip if no value in current search
    if (!value) continue

    // If route defines allowed values, validate current value(s)
    if (possibleValues) {
      // Handle both single string values and arrays
      const isValid = Array.isArray(value)
        ? value.every(
            (v) => typeof v === 'string' && possibleValues.includes(v),
          )
        : typeof value === 'string' && possibleValues.includes(value)

      if (isValid) {
        filtered[param] = value
      }
    } else {
      // No restrictions - preserve any value (string or array)
      filtered[param] = value
    }
  }

  return filtered
}
